import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { mensajeDe } from '../responder/utilidades';
import { Avatar } from './SelectorPersona';
import { SENTIMIENTOS, duracion } from './constantes';

/*
 * Panel del buzón: cuántos casos hay, cuánto se tarda en contestar y resolver, cómo se reparten.
 * Todas las cifras las cuenta el servidor (buzon.estadisticas), ninguna la IA.
 *
 * Gráficas: magnitudes en barras horizontales de un solo tono con su etiqueta y su número (la
 * categoría lleva su color solo en el punto, para identificarla, no en la barra); la evolución
 * semanal compara dos series (recibidos / resueltos) con leyenda, colores validados para claro y
 * oscuro (--bz-serie-1/2 en buzon.css) y un recuadro al pasar el ratón.
 */

export default function Estadisticas({ formId }) {
  const [estado, setEstado] = useState({ cargando: true, datos: null, error: null });

  useEffect(() => {
    let vivo = true;
    api.get('/buzon/estadisticas', { params: { form_id: formId || undefined } })
      .then(({ data }) => vivo && setEstado({ cargando: false, datos: data, error: null }))
      .catch((err) => vivo && setEstado({ cargando: false, datos: null, error: mensajeDe(err, 'No se pudieron cargar las estadísticas') }));
    return () => { vivo = false; };
  }, [formId]);

  if (estado.cargando) return <div className="bz-centro bz-estadisticas"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando estadísticas" /></div>;
  if (estado.error) return <div className="bz-centro bz-estadisticas"><p>{estado.error}</p></div>;
  const d = estado.datos;
  if (!d.total) return <div className="bz-centro bz-estadisticas"><p>Todavía no hay casos que contar.</p></div>;
  const sentimientos = Object.entries(d.sentimiento || {}).filter(([, n]) => n > 0);

  return (
    <div className="bz-estadisticas od-scroll">
      <div className="bz-tiles">
        <Tile valor={d.abiertos} rotulo="Abiertos" detalle={`de ${d.total} en total`} />
        <Tile valor={d.sin_asignar} rotulo="Sin asignar" tono={d.sin_asignar ? 'aviso' : null} />
        <Tile valor={d.vencidos} rotulo="Vencidos" tono={d.vencidos ? 'peligro' : null} />
        <Tile valor={duracion(d.horas_primera_respuesta)} rotulo="Primera respuesta" detalle="mediana" />
        <Tile valor={duracion(d.horas_resolucion)} rotulo="Resolución" detalle="mediana" />
        <Tile valor={d.a_tiempo === null ? '—' : `${d.a_tiempo} %`} rotulo="Resueltos a tiempo" detalle="de los que tenían plazo" />
      </div>

      <div className="bz-graficas">
        <section className="bz-tarjeta">
          <h3 className="bz-tarjeta-titulo">Casos por semana</h3>
          <Semanas semanas={d.por_semana} />
        </section>

        <section className="bz-tarjeta">
          <h3 className="bz-tarjeta-titulo">Por categoría</h3>
          <Barras filas={d.por_categoria.map((c) => ({ id: c.id || 'sin', nombre: c.nombre, n: c.n, punto: c.color }))} />
        </section>

        <section className="bz-tarjeta">
          <h3 className="bz-tarjeta-titulo">Por estado</h3>
          <Barras filas={d.por_estado.map((e) => ({ id: e.id, nombre: e.nombre, n: e.n }))} />
        </section>

        {d.por_prioridad.some((p) => p.n) && (
          <section className="bz-tarjeta">
            <h3 className="bz-tarjeta-titulo">Abiertos por prioridad</h3>
            <Barras filas={d.por_prioridad.map((p) => ({ id: p.id, nombre: p.nombre, n: p.n }))} />
          </section>
        )}

        {sentimientos.length > 0 && (
          <section className="bz-tarjeta">
            <h3 className="bz-tarjeta-titulo">Tono de los mensajes (Aurora IA)</h3>
            <Barras filas={sentimientos.map(([id, n]) => ({ id, nombre: SENTIMIENTOS[id], n }))} />
          </section>
        )}

        {d.por_persona.length > 0 && (
          <section className="bz-tarjeta bz-tarjeta-ancha">
            <h3 className="bz-tarjeta-titulo">Carga por persona</h3>
            <table className="bz-tabla">
              <thead>
                <tr><th scope="col">Persona</th><th scope="col">Abiertos</th><th scope="col">Vencidos</th><th scope="col">Resueltos</th></tr>
              </thead>
              <tbody>
                {d.por_persona.map((p) => (
                  <tr key={p.id}>
                    <td><span className="flex items-center gap-2"><Avatar persona={p} tam={22} />{p.nombre}</span></td>
                    <td>{p.abiertos}</td>
                    <td className={p.vencidos ? 'bz-texto-peligro' : ''}>{p.vencidos}</td>
                    <td>{p.resueltos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}

function Tile({ valor, rotulo, detalle, tono }) {
  return (
    <div className={`bz-tile ${tono ? `is-${tono}` : ''}`}>
      <span className="bz-tile-valor">{valor}</span>
      <span className="bz-tile-rotulo">{rotulo}</span>
      {detalle && <span className="bz-tile-detalle">{detalle}</span>}
    </div>
  );
}

function Barras({ filas }) {
  const max = Math.max(1, ...filas.map((f) => f.n));
  return (
    <ul className="bz-barras">
      {filas.map((f) => (
        <li key={f.id} className="bz-barra-fila" title={`${f.nombre}: ${f.n}`}>
          <span className="bz-barra-nombre">
            {f.punto && <span className="bz-punto" style={{ background: f.punto }} aria-hidden="true" />}
            {f.nombre}
          </span>
          <span className="bz-barra-pista" aria-hidden="true">
            <span className="bz-barra-dato" style={{ width: `${(f.n / max) * 100}%` }} />
          </span>
          <span className="bz-barra-n">{f.n}</span>
        </li>
      ))}
    </ul>
  );
}

const etiquetaSemana = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

function Semanas({ semanas }) {
  const [foco, setFoco] = useState(null);
  const max = Math.max(1, ...semanas.flatMap((s) => [s.creados, s.resueltos]));
  return (
    <div className="bz-semanas">
      <div className="bz-leyenda" aria-hidden="true">
        <span><i className="is-1" /> Recibidos</span>
        <span><i className="is-2" /> Resueltos</span>
      </div>
      <div className="bz-semanas-plot" onMouseLeave={() => setFoco(null)}>
        {semanas.map((s, i) => (
          <div
            key={s.semana}
            className={`bz-semana ${foco === i ? 'is-foco' : ''}`}
            onMouseEnter={() => setFoco(i)}
            onFocus={() => setFoco(i)}
            onBlur={() => setFoco(null)}
            tabIndex={0}
            aria-label={`Semana del ${etiquetaSemana(s.semana)}: ${s.creados} recibidos, ${s.resueltos} resueltos`}
          >
            <div className="bz-semana-barras">
              <span className="bz-col is-1" style={{ height: `${(s.creados / max) * 100}%` }} />
              <span className="bz-col is-2" style={{ height: `${(s.resueltos / max) * 100}%` }} />
            </div>
            <span className="bz-semana-eje">{etiquetaSemana(s.semana)}</span>
            {foco === i && (
              <div className="bz-tooltip" role="status">
                <b>Semana del {etiquetaSemana(s.semana)}</b>
                <span><i className="is-1" /> Recibidos <b>{s.creados}</b></span>
                <span><i className="is-2" /> Resueltos <b>{s.resueltos}</b></span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
